/**
 * Liquor and bar contracts (M5 task 3; 03-modules.md §11, 04-data-model.md "inventory and bar",
 * 05-api.md "Inventory and bar", 06-flows.md F8).
 *
 * Quantities: people type whole millilitres and bottles (`bottles`, `ml`, `fullBottles`,
 * `openMl`); everything read back from the stock ledger is **milli-ml** (`...Milli`: 750 ml is
 * 750000), the same unit inventory uses for an item counted in ml. Strength is basis points of
 * the volume (`abvBp: 4280` is 42.8%), like every rate in the system. Money is integer paise.
 */
import { z } from 'zod';
import { businessDateSchema, isoTimestampSchema } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';
import { nonNegativePaiseSchema, paiseSchema } from './money.js';
import { REPORT_FORMATS } from './reports.js';

// ---------------------------------------------------------------------------
// Vocabulary

export const LIQUOR_CATEGORIES = [
  'imfl',
  'beer',
  'wine',
  'country_liquor',
  'rtd',
  'other',
] as const;
export type LiquorCategory = (typeof LIQUOR_CATEGORIES)[number];
export const LIQUOR_CATEGORY_LABEL: Readonly<Record<LiquorCategory, string>> = {
  imfl: 'IMFL (spirits)',
  beer: 'Beer',
  wine: 'Wine',
  country_liquor: 'Country liquor',
  rtd: 'Ready to drink',
  other: 'Other',
};

export const PERMIT_STATUSES = ['available', 'assigned', 'void'] as const;
export type PermitStatus = (typeof PERMIT_STATUSES)[number];

/** How an order got its permit: `auto` when settled, `manual` by someone at the bar. */
export const PERMIT_ASSIGN_MODES = ['auto', 'manual'] as const;
export type PermitAssignMode = (typeof PERMIT_ASSIGN_MODES)[number];

/** `pending`: a liquor order is settled but no permit number was free; assign it later. */
export const ORDER_PERMIT_STATUSES = ['assigned', 'pending'] as const;
export type OrderPermitStatus = (typeof ORDER_PERMIT_STATUSES)[number];

export const BREAKAGE_REASONS = ['broken', 'spilled', 'leakage', 'tasting', 'other'] as const;
export type BreakageReason = (typeof BREAKAGE_REASONS)[number];
export const BREAKAGE_REASON_LABEL: Readonly<Record<BreakageReason, string>> = {
  broken: 'Bottle broken',
  spilled: 'Spilled',
  leakage: 'Leakage',
  tasting: 'Tasting or sample',
  other: 'Other',
};

export const BAR_REPORT_KEYS = ['excise', 'bar-consumption'] as const;
export type BarReportKey = (typeof BAR_REPORT_KEYS)[number];

const queryInt = (v: unknown) => (typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : v);
const formatField = z.enum(REPORT_FORMATS).default('json');
const noteSchema = z.string().trim().min(1).max(300);
const atLeastOne = (v: object) => Object.keys(v).length > 0;
const atLeastOneMsg = { message: 'Send at least one field to change' };

const MAX_COUNT_BOTTLES = 100_000;
const MAX_ML = 10_000_000;

// ---------------------------------------------------------------------------
// Liquor profiles

export const bottleMlSchema = z.int().min(1).max(20_000);
export const pegSizesSchema = z
  .array(z.int().min(1).max(5000))
  .max(8)
  .refine((a) => new Set(a).size === a.length, { message: 'Peg sizes must all be different' });
/** Strength in basis points of volume: 0 (soft drink) to 100% (10000). */
export const abvBpSchema = z.int().min(0).max(10_000);

const profileFields = {
  bottleMl: bottleMlSchema,
  pegSizesMl: pegSizesSchema,
  abvBp: abvBpSchema,
  exciseCode: z.string().trim().min(1).max(40).nullable(),
  category: z.enum(LIQUOR_CATEGORIES),
  brand: z.string().trim().min(1).max(80).nullable(),
  /** Printed price of one full bottle. */
  mrpPaise: nonNegativePaiseSchema.nullable(),
};

export const liquorProfileSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  stockItemId: uuidSchema,
  itemName: z.string(),
  bottleMl: z.int(),
  pegSizesMl: z.array(z.int()),
  abvBp: z.int(),
  exciseCode: z.string().nullable(),
  category: z.enum(LIQUOR_CATEGORIES),
  brand: z.string().nullable(),
  mrpPaise: paiseSchema.nullable(),
  archivedAt: isoTimestampSchema.nullable(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
});
export type LiquorProfileView = z.output<typeof liquorProfileSchema>;

export const createLiquorProfileSchema = z.object({
  id: uuidSchema.optional(),
  /** A stock item of kind `liquor` counted in ml (inventory). One profile per item. */
  stockItemId: uuidSchema,
  bottleMl: profileFields.bottleMl,
  pegSizesMl: profileFields.pegSizesMl.default([30, 60]),
  abvBp: profileFields.abvBp,
  exciseCode: profileFields.exciseCode.optional(),
  category: profileFields.category,
  brand: profileFields.brand.optional(),
  mrpPaise: profileFields.mrpPaise.optional(),
});
export const updateLiquorProfileSchema = z
  .object({
    bottleMl: profileFields.bottleMl,
    pegSizesMl: profileFields.pegSizesMl,
    abvBp: profileFields.abvBp,
    exciseCode: profileFields.exciseCode,
    category: profileFields.category,
    brand: profileFields.brand,
    mrpPaise: profileFields.mrpPaise,
  })
  .partial()
  .refine(atLeastOne, atLeastOneMsg);
export const liquorProfileParamsSchema = z.object({ outletId: uuidSchema, profileId: uuidSchema });
export const liquorProfileListSchema = z.object({ data: z.array(liquorProfileSchema) });

// ---------------------------------------------------------------------------
// Permits (the VHR permit number register)

export const permitNoSchema = z
  .string()
  .trim()
  .min(3)
  .max(40)
  .regex(/^[A-Za-z0-9][A-Za-z0-9/._ -]{1,38}[A-Za-z0-9]$/, {
    message: 'Letters, digits and / - . _ only',
  });

export const vhrPermitSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  permitNo: z.string(),
  holderName: z.string().nullable(),
  validFrom: businessDateSchema.nullable(),
  validTo: businessDateSchema.nullable(),
  status: z.enum(PERMIT_STATUSES),
  /** Past `validTo` (an available permit that has expired is not given out). */
  expired: z.boolean(),
  orderId: uuidSchema.nullable(),
  orderNo: z.int().nullable(),
  assignedAt: isoTimestampSchema.nullable(),
  voidReason: z.string().nullable(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
});
export type VhrPermitView = z.output<typeof vhrPermitSchema>;

export const permitBalanceSchema = z.object({
  available: z.int(),
  /** Available and not expired: what the next orders can use. */
  usable: z.int(),
  assigned: z.int(),
  void: z.int(),
  expired: z.int(),
  /** Liquor orders settled with no permit to give them. */
  pendingOrders: z.int(),
});
export type PermitBalance = z.output<typeof permitBalanceSchema>;

export const permitListQuerySchema = z.object({
  status: z.enum(PERMIT_STATUSES).optional(),
  q: z.string().trim().min(1).max(40).optional(),
  limit: z.preprocess(queryInt, z.number().int().min(1).max(200).default(50)),
  offset: z.preprocess(queryInt, z.number().int().min(0).max(1_000_000).default(0)),
});
export const permitListSchema = z.object({
  data: z.array(vhrPermitSchema),
  total: z.int(),
  nextOffset: z.int().nullable(),
  balance: permitBalanceSchema,
});

export const createPermitSchema = z
  .object({
    id: uuidSchema.optional(),
    permitNo: permitNoSchema,
    holderName: z.string().trim().min(1).max(120).nullable().optional(),
    validFrom: businessDateSchema.nullable().optional(),
    validTo: businessDateSchema.nullable().optional(),
  })
  .refine((v) => !v.validFrom || !v.validTo || v.validTo >= v.validFrom, {
    message: 'Valid to is before valid from',
    path: ['validTo'],
  });
export const updatePermitSchema = z
  .object({
    holderName: z.string().trim().min(1).max(120).nullable(),
    validFrom: businessDateSchema.nullable(),
    validTo: businessDateSchema.nullable(),
  })
  .partial()
  .refine(atLeastOne, atLeastOneMsg);
export const voidPermitSchema = z.object({ reason: noteSchema });
export const permitParamsSchema = z.object({ outletId: uuidSchema, permitId: uuidSchema });

/** File size cap: 5,000 numbers fit in well under this. */
export const PERMIT_IMPORT_MAX_BYTES = 1024 * 1024;

/**
 * `POST {o}/permits/import`: a CSV or Excel file of permit numbers (one per row, or a book as
 * prefix + from + to). With `dryRun` (the default) nothing is written and the answer is the
 * plan; otherwise the new numbers are added in one transaction when the file has no errors.
 * Numbers already in the register are skipped, never changed.
 */
export const permitImportRequestSchema = z.object({
  fileName: z.string().trim().min(1).max(200).optional(),
  format: z.enum(['csv', 'xlsx']),
  contentBase64: z
    .string()
    .min(4)
    .max(Math.ceil(PERMIT_IMPORT_MAX_BYTES / 3) * 4 + 4)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, { message: 'Expected base64' }),
  dryRun: z.boolean().default(true),
});

export const permitImportResultSchema = z.object({
  dryRun: z.boolean(),
  applied: z.boolean(),
  importId: uuidSchema.nullable(),
  summary: z.object({
    rows: z.int(),
    /** Numbers the file would add (or added). */
    added: z.int(),
    /** Numbers already in the register. */
    duplicates: z.int(),
    errors: z.int(),
  }),
  entries: z.array(
    z.object({
      row: z.int(),
      permitNo: z.string(),
      action: z.enum(['add', 'duplicate']),
    }),
  ),
  errors: z.array(z.object({ row: z.int(), field: z.string().nullable(), message: z.string() })),
  canApply: z.boolean(),
  /** Liquor orders that were waiting for a permit and got one from this import. */
  assignedPending: z.int(),
});
export type PermitImportResult = z.output<typeof permitImportResultSchema>;

// ---------------------------------------------------------------------------
// Permits on orders

export const permitItemSchema = z.object({
  stockItemId: uuidSchema,
  name: z.string(),
  brand: z.string().nullable(),
  category: z.enum(LIQUOR_CATEGORIES),
  exciseCode: z.string().nullable(),
  bottleMl: z.int(),
  abvBp: z.int(),
  /** Poured or sold, milli-ml. */
  qtyMilli: z.int(),
});
export type PermitItem = z.output<typeof permitItemSchema>;

export const orderPermitSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  orderId: uuidSchema,
  orderNo: z.int(),
  businessDate: businessDateSchema,
  status: z.enum(ORDER_PERMIT_STATUSES),
  permitId: uuidSchema.nullable(),
  permitNo: z.string().nullable(),
  mode: z.enum(PERMIT_ASSIGN_MODES).nullable(),
  items: z.array(permitItemSchema),
  totalMilli: z.int(),
  alcoholMilli: z.int(),
  assignedAt: isoTimestampSchema.nullable(),
  assignedBy: uuidSchema.nullable(),
  createdAt: isoTimestampSchema,
});
export type OrderPermitView = z.output<typeof orderPermitSchema>;

export const orderPermitQuerySchema = z.object({
  status: z.enum(ORDER_PERMIT_STATUSES).optional(),
  from: businessDateSchema.optional(),
  to: businessDateSchema.optional(),
  limit: z.preprocess(queryInt, z.number().int().min(1).max(200).default(50)),
  offset: z.preprocess(queryInt, z.number().int().min(0).max(1_000_000).default(0)),
});
export const orderPermitListSchema = z.object({
  data: z.array(orderPermitSchema),
  total: z.int(),
  nextOffset: z.int().nullable(),
});
export const orderPermitParamsSchema = z.object({ outletId: uuidSchema, orderId: uuidSchema });

/** `POST {o}/orders/{id}/permit`: give a settled liquor order its permit now. */
export const assignPermitSchema = z.object({
  /** A specific available number from the register; omitted, the next available one is used. */
  permitNo: permitNoSchema.optional(),
});

// ---------------------------------------------------------------------------
// Breakage

const bottlesField = z.int().min(0).max(MAX_COUNT_BOTTLES);
const mlField = z.int().min(0).max(MAX_ML);

export const recordBreakageSchema = z
  .object({
    id: uuidSchema.optional(),
    stockItemId: uuidSchema,
    /** Whole bottles lost. */
    bottles: bottlesField.default(0),
    /** Loose millilitres lost (added to the bottles). */
    ml: mlField.default(0),
    reason: z.enum(BREAKAGE_REASONS),
    note: noteSchema.optional(),
    /** Where the bottle was kept; the outlet's default section when omitted. */
    storageSectionId: uuidSchema.optional(),
  })
  .refine((v) => v.bottles > 0 || v.ml > 0, {
    message: 'Say how much was lost',
    path: ['ml'],
  });

export const breakageSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  stockItemId: uuidSchema,
  itemName: z.string(),
  bottleMl: z.int(),
  qtyMilli: z.int(),
  reason: z.enum(BREAKAGE_REASONS),
  note: z.string().nullable(),
  businessDate: businessDateSchema,
  storageSectionId: uuidSchema,
  valuePaise: paiseSchema,
  recordedBy: uuidSchema.nullable(),
  createdAt: isoTimestampSchema,
});
export type BreakageView = z.output<typeof breakageSchema>;

export const breakageQuerySchema = z.object({
  from: businessDateSchema,
  to: businessDateSchema,
  stockItemId: uuidSchema.optional(),
});
export const breakageListSchema = z.object({
  data: z.array(breakageSchema),
  totalMilli: z.int(),
  totalValuePaise: paiseSchema,
});

// ---------------------------------------------------------------------------
// Closing counts

export const closingCountSchema = z.object({
  stockItemId: uuidSchema,
  fullBottles: z.int().min(0).max(MAX_COUNT_BOTTLES),
  /** Millilitres left in the open bottle(s). */
  openMl: mlField,
});

/**
 * `POST {o}/bar/closing`: the night's physical count of the bar. Counts for the same business
 * date replace the earlier ones (the day is counted again). With `adjustStock`, someone with
 * `inventory.counts.approve` also brings the books in line (a `count_adjust` ledger row per
 * item); otherwise only the variance is recorded.
 */
export const recordBarClosingSchema = z.object({
  /** Defaults to the outlet's current business day. */
  businessDate: businessDateSchema.optional(),
  counts: z.array(closingCountSchema).min(1).max(500),
  adjustStock: z.boolean().default(false),
  note: noteSchema.optional(),
});

export const barClosingRowSchema = z.object({
  stockItemId: uuidSchema,
  itemName: z.string(),
  bottleMl: z.int(),
  /** The books now (all storage sections), milli-ml. */
  bookMilli: z.int(),
  /** The count, or null when this item is not counted yet. */
  count: z
    .object({
      id: uuidSchema,
      fullBottles: z.int(),
      openMl: z.int(),
      countedMilli: z.int(),
      /** The books when it was counted, milli-ml. */
      systemMilli: z.int(),
      varianceMilli: z.int(),
      adjusted: z.boolean(),
      recordedBy: uuidSchema.nullable(),
      recordedAt: isoTimestampSchema,
      version: z.int(),
    })
    .nullable(),
});
export const barClosingSchema = z.object({
  outletId: uuidSchema,
  businessDate: businessDateSchema,
  rows: z.array(barClosingRowSchema),
  counted: z.int(),
  missing: z.int(),
  /** True when every liquor item has a count: the day close no longer waits for the bar. */
  complete: z.boolean(),
  /** Whether the day close waits for counts (`bar.closing_required`). */
  required: z.boolean(),
});
export type BarClosingView = z.output<typeof barClosingSchema>;
export const barClosingQuerySchema = z.object({ date: businessDateSchema.optional() });

// ---------------------------------------------------------------------------
// Reports

export const exciseQuerySchema = z.object({
  from: businessDateSchema,
  to: businessDateSchema,
  format: formatField,
});

export const exciseRowSchema = z.object({
  stockItemId: uuidSchema,
  name: z.string(),
  brand: z.string().nullable(),
  category: z.enum(LIQUOR_CATEGORIES),
  exciseCode: z.string().nullable(),
  bottleMl: z.int(),
  abvBp: z.int(),
  mrpPaise: paiseSchema.nullable(),
  openingMilli: z.int(),
  receivedMilli: z.int(),
  soldMilli: z.int(),
  breakageMilli: z.int(),
  /** Wastage, count adjustments, opening balances and moves out; signed. */
  otherMilli: z.int(),
  closingBookMilli: z.int(),
  /** The last physical count in the range, if any. */
  countedMilli: z.int().nullable(),
  countedOn: businessDateSchema.nullable(),
  /** counted minus the books on that day. */
  varianceMilli: z.int().nullable(),
  soldAlcoholMilli: z.int(),
});
export type ExciseRow = z.output<typeof exciseRowSchema>;

export const exciseReportSchema = z.object({
  outletId: uuidSchema,
  outletName: z.string(),
  from: businessDateSchema,
  to: businessDateSchema,
  generatedAt: isoTimestampSchema,
  rows: z.array(exciseRowSchema),
  totals: z.object({
    openingMilli: z.int(),
    receivedMilli: z.int(),
    soldMilli: z.int(),
    breakageMilli: z.int(),
    closingBookMilli: z.int(),
    soldAlcoholMilli: z.int(),
    varianceMilli: z.int(),
  }),
  /** Liquor orders of the range with a permit, and settled ones still waiting for one. */
  permits: z.object({ assigned: z.int(), pending: z.int() }),
});
export type ExciseReport = z.output<typeof exciseReportSchema>;

export const barConsumptionQuerySchema = z.object({
  from: businessDateSchema,
  to: businessDateSchema,
  format: formatField,
});
export const barConsumptionRowSchema = z.object({
  stockItemId: uuidSchema,
  name: z.string(),
  brand: z.string().nullable(),
  category: z.enum(LIQUOR_CATEGORIES),
  bottleMl: z.int(),
  /** Sales net of voids. */
  soldMilli: z.int(),
  /** Whole pegs of each of the item's peg sizes in the volume sold. */
  pegs: z.array(z.object({ pegMl: z.int(), count: z.int() })),
  /** Sold, in bottles (milli-ml / bottle size, rounded to 2 decimals as hundredths). */
  bottlesHundredths: z.int(),
  breakageMilli: z.int(),
  /** Sold at the item's cost when it was sold. */
  costValuePaise: paiseSchema,
  soldAlcoholMilli: z.int(),
});
export const barConsumptionReportSchema = z.object({
  outletId: uuidSchema,
  outletName: z.string(),
  from: businessDateSchema,
  to: businessDateSchema,
  generatedAt: isoTimestampSchema,
  rows: z.array(barConsumptionRowSchema),
  byCategory: z.array(
    z.object({
      category: z.enum(LIQUOR_CATEGORIES),
      soldMilli: z.int(),
      costValuePaise: paiseSchema,
    }),
  ),
  byDay: z.array(z.object({ businessDate: businessDateSchema, soldMilli: z.int() })),
  totals: z.object({
    soldMilli: z.int(),
    breakageMilli: z.int(),
    costValuePaise: paiseSchema,
    soldAlcoholMilli: z.int(),
  }),
});
export type BarConsumptionReport = z.output<typeof barConsumptionReportSchema>;

// Frozen excise reports (03-modules.md §11 `generateExciseReport`, `excise_report_snapshot`).
export const generateExciseReportSchema = z.object({
  id: uuidSchema.optional(),
  from: businessDateSchema,
  to: businessDateSchema,
  note: noteSchema.optional(),
});
export const exciseSnapshotSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  /** 1, 2, 3 … per outlet, in the order they were generated. */
  number: z.int(),
  from: businessDateSchema,
  to: businessDateSchema,
  note: z.string().nullable(),
  generatedBy: uuidSchema.nullable(),
  generatedAt: isoTimestampSchema,
  report: exciseReportSchema,
});
export type ExciseSnapshotView = z.output<typeof exciseSnapshotSchema>;
export const exciseSnapshotSummarySchema = exciseSnapshotSchema.omit({ report: true }).extend({
  soldMilli: z.int(),
});
export const exciseSnapshotListSchema = z.object({ data: z.array(exciseSnapshotSummarySchema) });
export const exciseSnapshotParamsSchema = z.object({
  outletId: uuidSchema,
  snapshotId: uuidSchema,
});
export const exciseSnapshotQuerySchema = z.object({ format: formatField });

// ---------------------------------------------------------------------------
// Events

const MASTER_CHANGES = z.enum(['created', 'updated', 'archived']);

export const liquorProfileSavedV1 = defineEvent(
  'liquor_profile.saved',
  1,
  z.object({
    profileId: uuidSchema,
    stockItemId: uuidSchema,
    change: MASTER_CHANGES,
    version: z.int(),
  }),
);
export const permitSavedV1 = defineEvent(
  'permit.saved',
  1,
  z.object({
    permitId: uuidSchema,
    permitNo: z.string(),
    change: z.enum(['created', 'updated', 'voided']),
  }),
);
export const permitsImportedV1 = defineEvent(
  'permit.imported',
  1,
  z.object({
    importId: uuidSchema,
    rows: z.int(),
    added: z.int(),
    duplicates: z.int(),
    assignedPending: z.int(),
  }),
);
export const permitAssignedV1 = defineEvent(
  'permit.assigned',
  1,
  z.object({
    orderPermitId: uuidSchema,
    orderId: uuidSchema,
    permitId: uuidSchema,
    permitNo: z.string(),
    mode: z.enum(PERMIT_ASSIGN_MODES),
    businessDate: businessDateSchema,
  }),
);
/** A settled liquor order has no permit number to take: an alert for the bar and the manager. */
export const permitPendingV1 = defineEvent(
  'permit.pending',
  1,
  z.object({ orderPermitId: uuidSchema, orderId: uuidSchema, businessDate: businessDateSchema }),
);
export const breakageRecordedV1 = defineEvent(
  'breakage.recorded',
  1,
  z.object({
    breakageId: uuidSchema,
    stockItemId: uuidSchema,
    qtyMilli: z.int(),
    reason: z.enum(BREAKAGE_REASONS),
    valuePaise: paiseSchema,
    businessDate: businessDateSchema,
  }),
);
export const barClosingRecordedV1 = defineEvent(
  'bar_closing.recorded',
  1,
  z.object({
    businessDate: businessDateSchema,
    items: z.int(),
    varianceItems: z.int(),
    varianceMilli: z.int(),
    adjusted: z.boolean(),
  }),
);
export const exciseReportGeneratedV1 = defineEvent(
  'excise_report.generated',
  1,
  z.object({
    snapshotId: uuidSchema,
    number: z.int(),
    from: businessDateSchema,
    to: businessDateSchema,
  }),
);

export const BAR_EVENTS = [
  liquorProfileSavedV1,
  permitSavedV1,
  permitsImportedV1,
  permitAssignedV1,
  permitPendingV1,
  breakageRecordedV1,
  barClosingRecordedV1,
  exciseReportGeneratedV1,
] as const;
